import React, { useState, useEffect } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { usersAPI, authAPI } from '../lib/api';
import { useAuthStore } from '../lib/auth';
import { toast } from 'sonner';
import { ArrowLeft, User, Check, Globe, Sparkles, ArrowRight, Lock, Eye, EyeOff } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

// Avatar options matching backend
const AVATAR_OPTIONS = [
  { id: 'avatar_brain', name: 'Brainy', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=brain&backgroundColor=0a1172' },
  { id: 'avatar_lightning', name: 'Lightning', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=lightning&backgroundColor=0a1172' },
  { id: 'avatar_star', name: 'Star', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=star&backgroundColor=0a1172' },
  { id: 'avatar_trophy', name: 'Champion', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=trophy&backgroundColor=0a1172' },
  { id: 'avatar_book', name: 'Scholar', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=scholar&backgroundColor=0a1172' },
  { id: 'avatar_rocket', name: 'Rocket', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=rocket&backgroundColor=0a1172' },
  { id: 'avatar_flame', name: 'Flame', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=flame&backgroundColor=0a1172' },
  { id: 'avatar_crown', name: 'Royalty', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=crown&backgroundColor=0a1172' },
  { id: 'avatar_gem', name: 'Diamond', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=diamond&backgroundColor=0a1172' },
  { id: 'avatar_ninja', name: 'Ninja', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=ninja&backgroundColor=0a1172' },
  { id: 'avatar_owl', name: 'Owl', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=owl&backgroundColor=0a1172' },
  { id: 'avatar_phoenix', name: 'Phoenix', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=phoenix&backgroundColor=0a1172' },
];

// Common timezones
const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'UTC', label: 'UTC' },
];

export default function ProfileSettings() {
  useSEO('Profile Settings | JepPrep', 'Manage your JepPrep profile, display name, and account preferences.');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSetupMode = searchParams.get('setup') === 'true';
  const { user, setUser } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  
  // Form state
  const [displayName, setDisplayName] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState('avatar_brain');
  
  // Detect browser timezone and use as default
  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [timezone, setTimezone] = useState(detectedTimezone);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const isGoogleUser = profile?.auth_provider === 'google';

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await usersAPI.getMe();
      const profileData = response.data;
      setProfile(profileData);
      setDisplayName(profileData.display_name || '');
      setSelectedAvatar(profileData.avatar_id || 'avatar_brain');
      setTimezone(profileData.timezone || detectedTimezone);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        display_name: displayName,
        avatar_id: selectedAvatar,
        timezone: timezone,
      };

      const response = await usersAPI.updateMe(updateData);
      
      // Update local user state
      if (setUser && response.data) {
        setUser({
          ...user,
          display_name: displayName,
          avatar_id: selectedAvatar,
          avatar_url: AVATAR_OPTIONS.find(a => a.id === selectedAvatar)?.url,
          timezone: timezone,
        });
      }
      
      toast.success(isSetupMode ? 'Profile set up successfully! Welcome aboard!' : 'Profile updated successfully!');
      
      // Navigate to dashboard after setup
      if (isSetupMode) {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const getAvatarUrl = (avatarId) => {
    const avatar = AVATAR_OPTIONS.find(a => a.id === avatarId);
    return avatar?.url || AVATAR_OPTIONS[0].url;
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (currentPassword === newPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    setChangingPassword(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-gray-400">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              {!isSetupMode && (
                <Link to="/dashboard">
                  <Button variant="ghost" size="sm" className="text-gray-300">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                </Link>
              )}
              <img src={LOGO_URL} alt="JepPrep" className="h-12" />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto max-w-2xl px-4 py-8">
        {/* Welcome message for new users */}
        {isSetupMode && (
          <Card className="bg-gradient-to-r from-[hsl(var(--brand-gold-500))]/10 to-transparent border-[hsl(var(--brand-gold-500))]/30 mb-6">
            <CardContent className="py-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[hsl(var(--brand-gold-500))] flex items-center justify-center shrink-0">
                  <Sparkles className="w-6 h-6 text-[hsl(var(--brand-blue-900))]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">Welcome to JepPrep!</h2>
                  <p className="text-gray-300">
                    Let's personalize your experience. Choose an avatar that represents you and set your timezone for accurate streak tracking.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="text-2xl font-display text-white flex items-center gap-2">
              <User className="w-6 h-6 text-[hsl(var(--brand-gold-500))]" />
              {isSetupMode ? 'Set Up Your Profile' : 'Profile Settings'}
            </CardTitle>
            <CardDescription className="text-gray-400">
              {isSetupMode 
                ? 'Complete these steps to get started' 
                : 'Customize your JepPrep profile'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Avatar Selection */}
            <div className="space-y-4">
              <Label className="text-white text-lg">Choose Your Avatar</Label>
              <div className="flex items-center gap-6 mb-4">
                <div className="w-24 h-24 rounded-full bg-[hsl(var(--brand-blue-800))] border-4 border-[hsl(var(--brand-gold-500))] overflow-hidden">
                  <img 
                    src={getAvatarUrl(selectedAvatar)} 
                    alt="Selected avatar" 
                    className="w-full h-full object-cover"
                    data-testid="selected-avatar"
                  />
                </div>
                <div>
                  <p className="text-white font-medium">
                    {AVATAR_OPTIONS.find(a => a.id === selectedAvatar)?.name || 'Brainy'}
                  </p>
                  <p className="text-gray-400 text-sm">Your current avatar</p>
                </div>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                {AVATAR_OPTIONS.map((avatar) => (
                  <button
                    key={avatar.id}
                    onClick={() => setSelectedAvatar(avatar.id)}
                    className={`relative w-14 h-14 rounded-full overflow-hidden border-2 transition-all ${
                      selectedAvatar === avatar.id 
                        ? 'border-[hsl(var(--brand-gold-500))] ring-2 ring-[hsl(var(--brand-gold-500))] ring-offset-2 ring-offset-[hsl(var(--secondary))]' 
                        : 'border-[hsl(var(--border))] hover:border-[hsl(var(--brand-gold-400))]'
                    }`}
                    data-testid={`avatar-option-${avatar.id}`}
                  >
                    <img 
                      src={avatar.url} 
                      alt={avatar.name} 
                      className="w-full h-full object-cover"
                    />
                    {selectedAvatar === avatar.id && (
                      <div className="absolute inset-0 bg-[hsl(var(--brand-gold-500))]/20 flex items-center justify-center">
                        <Check className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Display Name */}
            <div className="space-y-2">
              <Label htmlFor="displayName" className="text-white">Display Name</Label>
              <Input
                id="displayName"
                type="text"
                placeholder="Your display name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white"
                data-testid="display-name-input"
              />
              <p className="text-xs text-gray-500">This is how other players will see you</p>
            </div>

            {/* Email (Read-only) */}
            <div className="space-y-2">
              <Label className="text-white">Email</Label>
              <Input
                type="email"
                value={profile?.email || ''}
                disabled
                className="bg-[hsl(var(--accent))]/50 border-[hsl(var(--border))] text-gray-400 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500">Email cannot be changed</p>
            </div>

            {/* Change Password — hidden for Google OAuth users */}
            {!isGoogleUser && !isSetupMode && (
              <div className="space-y-4 border-t border-[hsl(var(--border))] pt-6">
                <Label className="text-white text-lg flex items-center gap-2">
                  <Lock className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                  Change Password
                </Label>

                <div className="space-y-2">
                  <Label htmlFor="currentPassword" className="text-gray-300 text-sm">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      placeholder="Enter current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white pr-10"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                      data-testid="toggle-current-password"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-gray-300 text-sm">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="Enter new password (min 6 characters)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                      data-testid="toggle-new-password"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-gray-300 text-sm">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white"
                    data-testid="confirm-password-input"
                  />
                </div>

                <Button
                  onClick={handleChangePassword}
                  disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                  variant="outline"
                  className="border-[hsl(var(--brand-gold-500))]/50 text-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-gold-500))]/10"
                  data-testid="change-password-button"
                >
                  {changingPassword ? 'Updating...' : 'Update Password'}
                </Button>
              </div>
            )}

            {/* Timezone */}
            <div className="space-y-2">
              <Label htmlFor="timezone" className="text-white flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Timezone
              </Label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger 
                  className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white"
                  data-testid="timezone-select"
                >
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <SelectItem 
                      key={tz.value} 
                      value={tz.value}
                      className="text-white hover:bg-[hsl(var(--accent))]"
                    >
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Detected: {detectedTimezone}
                {timezone !== detectedTimezone && " (using custom)"}
              </p>
            </div>

            {/* Save Button */}
            <div className="pt-4 space-y-3">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-6 text-lg font-semibold"
                data-testid="save-profile-button"
              >
                {saving ? 'Saving...' : (isSetupMode ? 'Save & Continue' : 'Save Changes')}
              </Button>
              
              {isSetupMode && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="w-full border-[hsl(var(--border))] text-gray-300 hover:bg-[hsl(var(--accent))] py-6 text-lg"
                  data-testid="skip-setup-button"
                >
                  Skip for now
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
